import React, { useState, useEffect } from 'react';
import { channelApi } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FiPlus, FiCopy, FiCheck, FiRefreshCw, FiAlertCircle, FiSettings, FiActivity, FiKey } from 'react-icons/fi';
import { toast } from 'sonner';

export default function PlatformIntegration() {
  const [channels, setChannels] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editWebhookUrl, setEditWebhookUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchChannels = async () => {
    setIsLoading(true);
    try {
      const response = await channelApi.get('/admin/list');
      if (response.data?.status === 200) {
        setChannels(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal mengambil daftar channel integrasi.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await channelApi.get('/admin/logs');
      if (response.data?.status === 200) {
        setLogs(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
    fetchLogs();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!newPlatformName.trim()) {
      toast.warning('Nama platform mitra wajib diisi.');
      return;
    }

    try {
      const response = await channelApi.post('/admin/register', {
        channel_name: newPlatformName,
        webhook_url: newWebhookUrl
      });

      if (response.data?.status === 201) {
        toast.success('Platform distribusi berhasil didaftarkan!');
        setGeneratedCredentials(response.data.data);
        setNewPlatformName('');
        setNewWebhookUrl('');
        fetchChannels();
        fetchLogs();
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal mendaftarkan platform mitra.');
    }
  };

  const handleUpdateWebhook = async () => {
    if (!editingChannel) return;

    setIsSaving(true);
    try {
      const response = await channelApi.put('/admin/webhook', {
        channel_name: editingChannel.channel_name,
        webhook_url: editWebhookUrl,
      });

      if (response.data?.status === 200) {
        toast.success('Konfigurasi webhook berhasil diperbarui!');
        setIsEditDialogOpen(false);
        setEditingChannel(null);
        fetchChannels();
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memperbarui konfigurasi channel.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(type);
    toast.success(`${type} berhasil disalin ke clipboard!`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="flex flex-col gap-6">
      
      {/* Header and Quick Stats */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <p className="mb-1 text-xs font-semibold text-slate-500">Product Manager / Integrasi API Mitra</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Integrasi API Mitra</h1>
          <p className="text-sm text-slate-500">Hubungkan PRIME dengan platform eksternal dan kelola koneksi integrasi.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchChannels(); fetchLogs(); }} className="gap-2">
            <FiRefreshCw className={isLoading || logsLoading ? 'animate-spin' : ''} />
            Refresh Data
          </Button>
          <Button size="sm" onClick={() => setIsDialogOpen(true)} className="gap-2 bg-brand-500 hover:bg-brand-600">
            <FiPlus />
            Onboard Platform Baru
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-[#E0F2FE] p-3 text-[#0369A1]">
                <FiKey className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Koneksi Aktif</p>
                <h3 className="text-2xl font-bold text-slate-800">{channels.filter(c => c.status === 'Active').length} Platform</h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-50 p-3 text-green-600">
                <FiActivity className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sync Status</p>
                <h3 className="text-2xl font-bold text-slate-800">
                  {channels.length > 0 ? Math.round((channels.filter(c => c.status === 'Active').length / channels.length) * 100) : 100}% Online
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-50 p-3 text-indigo-600">
                <FiSettings className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Transaksi</p>
                <h3 className="text-2xl font-bold text-slate-800">{channels.reduce((sum, c) => sum + (c.transactionsCount || 0), 0)} Tx</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main List */}
      <Card className="border-0 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-bold text-slate-700">Daftar Platform Distribusi Terkoneksi</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold">Platform / Channel Name</TableHead>
                <TableHead className="font-bold">Client ID</TableHead>
                <TableHead className="font-bold">Webhook Sync Endpoint</TableHead>
                <TableHead className="font-bold">Total Transaksi</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="font-bold text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-slate-400">Loading channels...</TableCell>
                </TableRow>
              ) : channels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-slate-400">Belum ada platform mitra yang terhubung.</TableCell>
                </TableRow>
              ) : (
                channels.map((channel) => (
                  <TableRow key={channel.channel_id} className="hover:bg-slate-50/50">
                    <TableCell className="font-semibold text-slate-700">
                      {channel.channel_name}
                      {/* env comes from the credential row, not from this server: one
                          production host serves sandbox and production keys alike, so
                          without the label a test key looks like a live one. */}
                      {channel.env && (
                        <Badge className={`ml-2 text-[9px] h-4 px-1.5 align-middle ${
                          channel.env === 'sandbox'
                            ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/15'
                            : 'bg-slate-500/10 text-slate-500 hover:bg-slate-500/15'
                        }`}>
                          {channel.env}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{channel.client_id}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 text-xs" title={channel.webhook_url}>
                      {channel.webhook_url || '-'}
                    </TableCell>
                    <TableCell className="font-bold">{channel.transactionsCount || 0} Booking</TableCell>
                    <TableCell>
                      <Badge className={channel.status === 'Active' ? 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}>
                        {channel.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="xs" onClick={() => {
                        setEditingChannel(channel);
                        setEditWebhookUrl(channel.webhook_url || '');
                        setShowApiKey(false);
                        setIsEditDialogOpen(true);
                      }} className="text-[#0369A1] hover:bg-[#F0F9FF]">
                        Edit Config
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* API Logs Audit Trail */}
      <Card className="border-0 bg-white shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-bold text-slate-700">Audit Trail: Log Keluar Masuk Webhook (API Hub)</CardTitle>
          <Badge className="bg-blue-50 text-blue-600">Observability Active</Badge>
        </CardHeader>
        <CardContent>
          <div className="max-h-[350px] overflow-y-auto rounded-lg border border-slate-100">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50">
                <TableRow>
                  <TableHead className="font-bold">Arah</TableHead>
                  <TableHead className="font-bold">Platform / Supplier</TableHead>
                  <TableHead className="font-bold">Endpoint</TableHead>
                  <TableHead className="font-bold">HTTP Code</TableHead>
                  <TableHead className="font-bold">Status Payload</TableHead>
                  <TableHead className="font-bold">Waktu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-slate-400">Loading audit logs...</TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-slate-400">Belum ada aktivitas API Hub terekam.</TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <TableRow key={log.log_id || index} className="hover:bg-slate-50/50">
                      <TableCell>
                        <Badge className={log.direction === 'INBOUND' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#EFF6FF] text-[#2563EB]'}>
                          {log.direction}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-700">{log.supplierName}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{log.endpoint}</TableCell>
                      <TableCell className="font-mono font-bold text-xs">{log.responseCode || 200}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={log.status === 'Success' ? 'border-emerald-200 text-emerald-600 bg-emerald-50/50' : 'border-amber-200 text-amber-600 bg-amber-50/50'}>
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-400">
                        {new Date(log.timestamp).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if(!open) setGeneratedCredentials(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Koneksikan Platform Distribusi Baru</DialogTitle>
          </DialogHeader>
          
          {!generatedCredentials ? (
            <form onSubmit={handleRegister} className="space-y-4 pt-2">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Nama Platform Mitra (OTA)</label>
                <Input 
                  value={newPlatformName}
                  onChange={(e) => setNewPlatformName(e.target.value)}
                  placeholder="Contoh: GOERS Partner, Mitra OTA"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Webhook Sync URL (Opsional)</label>
                <Input 
                  value={newWebhookUrl}
                  onChange={(e) => setNewWebhookUrl(e.target.value)}
                  placeholder="https://api.partner.com/webhook/sync"
                />
                <p className="text-[10px] text-slate-400">PRIME akan otomatis mengirimkan payload sinkronisasi harga/kuota real-time ke URL ini.</p>
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Batal</Button>
                <Button type="submit" className="bg-brand-500 hover:bg-brand-600">Generate API Credentials</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4 pt-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-emerald-700 flex items-start gap-2.5">
                <FiCheck className="mt-1 h-5 w-5 shrink-0" />
                <div>
                  <h4 className="font-bold text-sm">Kredensial API Berhasil Dibuat!</h4>
                  <p className="text-xs mt-1">Salin Kunci Akses berikut. Demi keamanan, API key hanya akan diperlihatkan sekali saja di layar ini.</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">CLIENT ID</span>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <span className="flex-1 font-mono text-xs truncate">{generatedCredentials.client_id}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(generatedCredentials.client_id, 'Client ID')}>
                      {copiedKey === 'Client ID' ? <FiCheck className="text-emerald-500" /> : <FiCopy />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase">API KEY (X-API-KEY)</span>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <span className="flex-1 font-mono text-xs truncate">{generatedCredentials.api_key}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(generatedCredentials.api_key, 'API Key')}>
                      {copiedKey === 'API Key' ? <FiCheck className="text-emerald-500" /> : <FiCopy />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 p-3 text-amber-700 text-xs flex gap-2">
                <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p><strong>Peringatan Keamanan:</strong> Jangan bagikan API Key ini di saluran publik. Simpan di vault konfigurasi server milik platform mitra.</p>
              </div>

              <DialogFooter className="pt-2">
                <Button type="button" className="w-full bg-slate-800 text-white" onClick={() => { setIsDialogOpen(false); setGeneratedCredentials(null); }}>
                  Selesai & Tutup
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Config Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        if (!open && isSaving) return;
        if (!open) setEditingChannel(null);
        setIsEditDialogOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Konfigurasi Channel</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Channel Name (read-only) */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600">Nama Platform / Channel</label>
              <div className="rounded-lg border border-slate-200 bg-slate-100 p-2.5 text-sm font-semibold text-slate-700">
                {editingChannel?.channel_name || '-'}
              </div>
            </div>

            {/* API Key — server returns only the last 4 digits; the full key is shown
                once, at register time, and is not recoverable afterwards. */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600">API Key (X-API-KEY)</label>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                <span className="flex-1 font-mono text-xs select-none">
                  {editingChannel?.api_key_masked || '••••'}
                </span>
              </div>
              <p className="text-[11px] text-slate-500">
                Key lengkap hanya ditampilkan sekali saat channel dibuat. Kalau hilang, daftarkan ulang channel untuk mendapat key baru.
              </p>
            </div>

            {/* Webhook URL (editable) */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600">Webhook Sync URL</label>
              <Input
                value={editWebhookUrl}
                onChange={(e) => setEditWebhookUrl(e.target.value)}
                placeholder="https://api.partner.com/webhook/sync"
              />
              <p className="text-[10px] text-slate-400">PRIME akan otomatis mengirimkan payload notifikasi real-time ke URL ini.</p>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Batal
              </Button>
              <Button
                type="button"
                className="bg-brand-500 hover:bg-brand-600"
                disabled={isSaving}
                onClick={handleUpdateWebhook}
              >
                {isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
